import { extractPalette, type PaletteColor } from "@cva/shared";
import { useSyncExternalStore } from "react";

/**
 * "Farben aus Bild" in the browser: the image is drawn onto a small canvas
 * and its pixels go to `extractPalette` (packages/shared/src/brand/palette.ts).
 */

/** Longest side of the analysed image; enough for the dominant colours. */
const SAMPLE_SIZE = 96;

function drawSample(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
): Uint8ClampedArray {
  const scale = Math.min(1, SAMPLE_SIZE / Math.max(sourceWidth, sourceHeight, 1));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("Canvas nicht verfügbar");
  context.drawImage(source, 0, 0, width, height);
  return context.getImageData(0, 0, width, height).data;
}

async function pixelsOf(blob: Blob): Promise<Uint8ClampedArray> {
  if (blob.type !== "image/svg+xml") {
    const bitmap = await createImageBitmap(blob);
    try {
      return drawSample(bitmap, bitmap.width, bitmap.height);
    } finally {
      bitmap.close();
    }
  }
  // SVGs cannot go through createImageBitmap everywhere: decode them via an <img>.
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return drawSample(image, image.naturalWidth || SAMPLE_SIZE, image.naturalHeight || SAMPLE_SIZE);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function paletteFromBlob(blob: Blob, count = 6): Promise<PaletteColor[]> {
  return extractPalette(await pixelsOf(blob), { count });
}

export async function paletteFromUrl(url: string, count = 6): Promise<PaletteColor[]> {
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) throw new Error("Das Bild konnte nicht geladen werden.");
  return paletteFromBlob(await response.blob(), count);
}

// ---------------------------------------------------------------------------
// Colours taken from images, offered in every colour picker ("Aus Bild")
// ---------------------------------------------------------------------------

const PALETTE_LIMIT = 12;
let imageColors: string[] = [];
const listeners = new Set<() => void>();

export function addImageColors(colors: readonly string[]) {
  const next = [...colors, ...imageColors.filter((color) => !colors.includes(color))];
  imageColors = next.slice(0, PALETTE_LIMIT);
  for (const listener of listeners) listener();
}

export function useImageColors(): string[] {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => imageColors,
  );
}
